import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowRight, CalendarClock, Mail, Tag, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackCta } from "@/lib/analytics";
import { useLocalizedHref } from "@/i18n/useLocalizedHref";

// Sticky bottom CTA bar for mobile only.
// Primary action + a small menu with the three things visitors actually want:
// book a meeting, ask for a price, or just send an email.
// Hidden on /kontakt (the form is already there) and when the user is near
// the bottom of the page (so it doesn't fight the footer).
export function StickyMobileCTA() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const loc = useLocalizedHref();
  const contactHref = loc("/kontakt");
  const isEn = pathname === "/en" || pathname.startsWith("/en/");
  const isFoto = pathname.startsWith("/foto");
  const pricingHref = loc("/priser");
  const isPricing = pathname === "/priser" || pathname.startsWith("/pris/") || pathname === "/en/pricing";
  // Ankaren skiljer sig mellan språken: EN-formuläret har id="contact".
  const contactHash = isEn ? "contact" : "kontaktformular";
  const pricingHash = isEn ? undefined : "prisforslag";
  // Skicka besökaren direkt till rätt formulär — utan mellanlandning.
  const targetTo = isPricing ? pricingHref : contactHref;
  const targetHash = isPricing ? pricingHash : contactHash;
  const targetSearch = isFoto ? { omrade: "foto" as const } : undefined;
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let raf = 0;
    const compute = () => {
      raf = 0;
      const y = window.scrollY;
      const doc = document.documentElement;
      const max = Math.max(1, (doc.scrollHeight || 0) - window.innerHeight);
      const nearBottom = y > max - 240;
      setVisible(y > 280 && !nearBottom);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pathname]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!visible) setOpen(false);
  }, [visible]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (pathname === contactHref) return null;

  const primaryLabel = isFoto
    ? isEn
      ? "Book a photographer"
      : "Boka fotograf"
    : isPricing
      ? isEn
        ? "Get a quote"
        : "Få prisförslag"
      : t("sticky.book");

  const copy = {
    more: isEn ? "More options" : "Fler val",
    close: isEn ? "Close options" : "Stäng valen",
    meeting: isEn ? "Book a meeting" : "Boka möte",
    meetingHint: isEn ? "15 min, no strings attached" : "15 min, helt förutsättningslöst",
    price: isEn ? "Ask for a price" : "Be om pris",
    priceHint: isEn ? "Quote within 24 hours" : "Prisförslag inom 24 timmar",
    mail: isEn ? "Email us" : "Mejla oss",
    mailHint: "hey@fenny.se",
  };

  const options = [
    {
      key: "sticky_meeting" as const,
      icon: CalendarClock,
      label: copy.meeting,
      hint: copy.meetingHint,
      to: contactHref,
      hash: contactHash,
      search: (isFoto ? { omrade: "foto" } : { omrade: "film" }) as never,
    },
    {
      key: "sticky_price" as const,
      icon: Tag,
      label: copy.price,
      hint: copy.priceHint,
      to: pricingHref,
      hash: pricingHash,
      search: undefined as never,
    },
  ];

  return (
    <div
      ref={wrapRef}
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-3 z-40 px-4 transition-all duration-300 md:hidden ${
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-4 opacity-0"
      }`}
    >
      <div className="mx-auto max-w-md">
        <div
          className={`overflow-hidden transition-all duration-300 ${
            open ? "mb-2 max-h-72 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="rounded-2xl border border-white/10 bg-background/95 p-1.5 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)] backdrop-blur">
            {options.map((o) => (
              <Link
                key={o.key}
                to={o.to as string}
                search={o.search}
                hash={o.hash}
                onClick={() => {
                  setOpen(false);
                  trackCta(o.key, { location: "sticky_mobile_menu" });
                }}
                className="flex items-center gap-3 rounded-xl px-3 py-3 text-left active:bg-white/10"
              >
                <o.icon className="h-4.5 w-4.5 shrink-0 text-mint" aria-hidden />
                <span className="min-w-0">
                  <span className="block text-[15px] font-medium leading-tight">{o.label}</span>
                  <span className="block truncate text-xs text-muted-foreground">{o.hint}</span>
                </span>
              </Link>
            ))}
            <a
              href="mailto:hey@fenny.se"
              onClick={() => {
                setOpen(false);
                trackCta("sticky_mail", { location: "sticky_mobile_menu" });
              }}
              className="flex items-center gap-3 rounded-xl px-3 py-3 text-left active:bg-white/10"
            >
              <Mail className="h-4.5 w-4.5 shrink-0 text-mint" aria-hidden />
              <span className="min-w-0">
                <span className="block text-[15px] font-medium leading-tight">{copy.mail}</span>
                <span className="block truncate text-xs text-muted-foreground">{copy.mailHint}</span>
              </span>
            </a>
          </div>
        </div>

        <div className="flex items-stretch gap-2">
          <Link
            to={targetTo as string}
            search={targetSearch as never}
            hash={targetHash}
            onClick={() => {
              setOpen(false);
              trackCta("book_strategy_meeting", { location: "sticky_mobile_cta" });
            }}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-mint px-5 py-3.5 text-[15px] font-semibold text-mint-foreground shadow-[0_12px_40px_-12px] shadow-mint/60"
          >
            {primaryLabel}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? copy.close : copy.more}
            onClick={() => {
              const next = !open;
              setOpen(next);
              if (next) trackCta("sticky_more", { location: "sticky_mobile_cta" });
            }}
            className="flex w-14 shrink-0 items-center justify-center rounded-full border border-white/15 bg-background/95 text-foreground shadow-[0_12px_40px_-16px_rgba(0,0,0,0.8)] backdrop-blur active:bg-white/10"
          >
            {open ? (
              <X className="h-5 w-5" aria-hidden />
            ) : (
              <span className="text-lg leading-none" aria-hidden>
                ⋯
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
